import { useState } from 'react';
import { poTotalJmd } from '../../lib/purchaseOrders';
import { formatJMD } from '../../lib/finances';
import { vendorStats } from '../../lib/vendorStats';
import { vendorWebsiteUrl } from '../../lib/vendors';
import Badge from '../Badge';

const STATUS_TONE = { Ordered: 'neutral', Shipped: 'warning', Received: 'success', Cancelled: 'muted' };

function Stars({ rating }) {
  if (!rating) return null;
  return <span className="vendor-stars">{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</span>;
}

// Orders come from the parent, which already has them all loaded — the card
// used to fetch its own on expand, which meant a query per vendor opened and
// no way to show spend or delivery speed until you opened it.
export default function VendorCard({ vendor, orders = [], onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const stats = vendorStats(vendor, orders);
  const websiteHref = vendorWebsiteUrl(vendor.website);

  const leadTime =
    stats.actualLeadTime != null
      ? `${stats.actualLeadTime}d actual`
      : stats.statedLeadTime != null
        ? `${stats.statedLeadTime}d quoted`
        : null;

  return (
    <div className="vendor-card">
      <div className="vendor-card-header" onClick={() => setExpanded((v) => !v)}>
        <div className="vendor-card-identity">
          <div className="vendor-card-title">
            {vendor.name}
            <Stars rating={vendor.rating} />
          </div>
          {vendor.supplies && <div className="vendor-card-supplies">{vendor.supplies}</div>}
        </div>

        {/* The four questions a vendor record should answer, on the face of
            the card rather than hidden behind an expand. */}
        <dl className="vendor-figures">
          <div>
            <dt>Spent</dt>
            <dd>{stats.orderCount ? formatJMD(stats.totalSpendJmd) : '—'}</dd>
          </div>
          <div>
            <dt>Orders</dt>
            <dd>
              {stats.orderCount || '—'}
              {stats.openOrders > 0 && <span className="vendor-open">{stats.openOrders} open</span>}
            </dd>
          </div>
          <div>
            <dt>Delivery</dt>
            <dd>{leadTime || '—'}</dd>
          </div>
          <div>
            <dt>Last order</dt>
            <dd>{stats.lastOrderDate || '—'}</dd>
          </div>
        </dl>
      </div>

      {expanded && (
        <div className="vendor-card-body">
          {(vendor.contact_name ||
            vendor.email ||
            vendor.phone ||
            vendor.website ||
            vendor.minimum_order) && (
            <div className="vendor-contact-row">
              {vendor.contact_name && <span>{vendor.contact_name}</span>}
              {vendor.email && (
                <a href={`mailto:${vendor.email}`} className="vendor-link">
                  {vendor.email}
                </a>
              )}
              {vendor.phone && <span>{vendor.phone}</span>}
              {/* Falls back to plain text if the URL isn't http(s) — see
                  vendorWebsiteUrl. noreferrer so the supplier's site can't
                  see where the link came from. */}
              {vendor.website &&
                (websiteHref ? (
                  <a
                    href={websiteHref}
                    className="vendor-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {vendor.website}
                  </a>
                ) : (
                  <span>{vendor.website}</span>
                ))}
              {vendor.minimum_order && <span>MOQ {vendor.minimum_order}</span>}
            </div>
          )}

          {/* Quoted vs measured, side by side — the gap is the useful part,
              and it only exists once there are received orders to measure. */}
          {(stats.statedLeadTime != null || stats.actualLeadTime != null) && (
            <p className="vendor-leadtime-note">
              {stats.statedLeadTime != null
                ? `Quoted ${stats.statedLeadTime} days`
                : 'No quoted lead time'}
              {stats.actualLeadTime != null
                ? ` · averaged ${stats.actualLeadTime} days across ${stats.deliveriesMeasured} ${
                    stats.deliveriesMeasured === 1 ? 'delivery' : 'deliveries'
                  }`
                : ' · no received orders to measure against yet'}
            </p>
          )}

          {vendor.notes && <p className="vendor-card-notes">{vendor.notes}</p>}

          <h4 className="vendor-orders-heading">Order history</h4>
          {orders.length === 0 ? (
            <p className="vendor-orders-empty">No purchase orders from this vendor yet.</p>
          ) : (
            <table className="vendor-orders-table">
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((po) => (
                  <tr key={po.id}>
                    <td>{po.po_number}</td>
                    <td>{po.order_date}</td>
                    <td>
                      <Badge tone={STATUS_TONE[po.status] || 'neutral'}>{po.status}</Badge>
                    </td>
                    <td>{formatJMD(poTotalJmd(po.purchase_order_items || [], po.exchange_rate))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="vendor-card-actions">
            <button className="ghost-button" onClick={() => onEdit(vendor)}>
              Edit
            </button>
            <button className="vendor-card-delete" onClick={() => onDelete(vendor.id)}>
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
